import { Component,Input, Output, EventEmitter, OnInit, AfterViewInit, ViewChild, ElementRef, SimpleChanges, OnChanges, Inject } from '@angular/core';
import { AttendanceHourLimitService } from './attendance-hour-limit.service';
import { MatDialog } from '@angular/material/dialog';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { BreakpointObserver, BreakpointState, Breakpoints } from '@angular/cdk/layout';
import { Router } from '@angular/router';
import { catchError, of } from 'rxjs';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';
import { AttendenceExportService } from '../../attendance-export.service';

@Component({
  selector: 'app-attendance-hour-limit',
  standalone: false,
  templateUrl: './attendance-hour-limit.component.html',
  styleUrls: ['./attendance-hour-limit.component.scss']
})
export class AttendanceHourLimitComponent implements OnInit, OnChanges, AfterViewInit {
  isHandset: boolean = false;
  
  @Input() proceedData: any; // Input for the API response data
  @Input() attendanceHeader: any; // Input for header data from session
  @Output() proceed = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  @ViewChild('exportTable') exportTable!: ElementRef<HTMLTableElement>;

  groupedData: any[] = [];
  payrollGroup: string = '';
  year: string = '';
  week: string = '';
  dateRange: string = '';
  selectAll: boolean = false;

  includedIds = new Set<number>();

  constructor(
    private attendanceHourLimitService: AttendanceHourLimitService,
    private attendenceExportService: AttendenceExportService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private snackBarService: SnackBarService,
    private router: Router
  ) {
    console.log('=== ATTENDANCE HOUR LIMIT CONSTRUCTOR CALLED ===');
  }

  ngOnInit(): void {
    console.log('=== ATTENDANCE HOUR LIMIT ngOnInit CALLED ===');
    this.breakpointObserver.observe([Breakpoints.Handset]).subscribe((state: BreakpointState) => {
      this.isHandset = state.matches;
    });

    // Restore previous state if available
    const savedState = sessionStorage.getItem('attendance_hour_limit_state');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        this.proceedData = state.proceedData;
        this.attendanceHeader = state.attendanceHeader;
        this.groupedData = state.groupedData;
        this.includedIds = new Set(state.includedIds);
        this.selectAll = state.selectAll;
        sessionStorage.removeItem('attendance_hour_limit_state');
      } catch {}
    }

    // Restore previous filter state if available
    const savedFilterState = sessionStorage.getItem('attendance_hour_limit_filter_state');
    if (savedFilterState) {
      try {
        const state = JSON.parse(savedFilterState);
        this.attendanceHeader = state.attendanceHeader;
        // Patch other dropdowns/fields as needed
        // Example: this.filterForm.patchValue(state.filterForm);
        sessionStorage.removeItem('attendance_hour_limit_filter_state');
      } catch {}
    }

    this.initIncludedIds();
    this.updateSelectAllState();
  }

  ngAfterViewInit(): void {
    console.log('=== ngAfterViewInit called ===');
    // Process data after view is initialized
    setTimeout(() => {
      this.processData();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('=== ngOnChanges triggered ===', changes);
    if (changes['proceedData']) {
      console.log('proceedData changed:', changes['proceedData']);
      // Use setTimeout to ensure change detection has completed
      setTimeout(() => {
        this.processData();
      }, 0);
    }
  }

  private processData(): void {
    console.log('=== ATTENDANCE HOUR LIMIT processData ===');
    console.log('proceedData:', this.proceedData);
    console.log('attendanceHeader:', this.attendanceHeader);
    
    // Fallback to sessionStorage if input not provided
    if (!this.attendanceHeader) {
      const raw = sessionStorage.getItem('attendance_export_header');
      if (raw) {
        try {
          this.attendanceHeader = JSON.parse(raw);
          console.log('Loaded attendanceHeader from sessionStorage:', this.attendanceHeader);
        } catch { }
      }
    }

    const exportedData = this.proceedData?.data?.employee_attendance_export;
    console.log('exportedData:', exportedData);
    console.log('exportedData length:', exportedData?.length);
    
    if (exportedData && exportedData.length) {
      console.log('Processing export data...');
      this.processExportData(exportedData);
      console.log('groupedData after processing:', this.groupedData);
    } else {
      console.log('No export data to process');
    }
  }

  processExportData(exportedData: any[]) {
    console.log('processExportData called with:', exportedData.length, 'records');
    
    const firstRecord = exportedData[0];
    this.payrollGroup = firstRecord.payrollgroup_code || '';
    this.year = firstRecord.shift_start_date ? firstRecord.shift_start_date.substring(0, 4) + '-' + (parseInt(firstRecord.shift_start_date.substring(0, 4)) + 1).toString().substring(2) : '';
    this.week = this.attendanceHeader?.weekNo || '33';
    
    // Calculate date range
    const dates = exportedData.map((d: any) => d.shift_start_date).sort();
    if (dates.length) {
      this.dateRange = `${this.formatDate(dates[0])} - ${this.formatDate(dates[dates.length - 1])}`;
    }

    // Group by shop_name but keep individual records
    const grouped: { [key: string]: any[] } = {};
    exportedData.forEach((record: any) => {
      const shopName = record.shop_name || 'Unknown Shop';
      if (!grouped[shopName]) {
        grouped[shopName] = [];
      }
      // Force all records to be checked (included) by default
      record.is_included = '1'; // Always set to '1' (included) by default
      record.isChecked = true; // Always checked
      grouped[shopName].push(record);
    });

    // Convert to array format for template
    this.groupedData = Object.keys(grouped).map(shopName => ({
      shopName,
      records: grouped[shopName]
    }));
    
    // Set selectAll to true since all are checked by default
    this.selectAll = true;
    
    console.log('Grouped data created:', this.groupedData.length, 'shops');
    console.log('Total records across all shops:', exportedData.length);
    this.groupedData.forEach((shop, idx) => {
      console.log(`Shop ${idx + 1}: ${shop.shopName} - ${shop.records.length} records`);
      shop.records.forEach((rec: any, recIdx: number) => {
        console.log(`  Record ${recIdx + 1}:`, this.getEmployeeName(rec), rec.shift_start_date, rec.shift_start_time);
      });
    });
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }

  getEmployeeName(record: any): string {
    return `${record.f_name} ${record.m_name} ${record.l_name}`.trim();
  }

  getRowNumber(shopIndex: number, recordIndex: number): number {
    let count = 0;
    for (let i = 0; i < shopIndex; i++) {
      count += this.groupedData[i].records.length;
    }
    return count + recordIndex + 1;
  }

  formatTime(timeStr: string): string {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  }

  onBack() {
    // Save current state to sessionStorage before emitting back
    sessionStorage.setItem('attendance_hour_limit_state', JSON.stringify({
      proceedData: this.proceedData,
      attendanceHeader: this.attendanceHeader,
      groupedData: this.groupedData,
      includedIds: Array.from(this.includedIds),
      selectAll: this.selectAll
    }));
    this.back.emit();
  }

  async onProceed() {
    const allIds: number[] = [];
    if (this.groupedData) {
      this.groupedData.forEach(shop => {
        shop.records.forEach((record: any) => {
          allIds.push(record.temp_export_id);
        });
      });
    }
    if (this.includedIds.size < allIds.length) {
      const dialogData = new ConfirmDialogModel(
        "Confirmation",
        "Unselected work hours will not be exported to the csv. Do you want to continue?"
      );
      const result = await this.dialog.open(ConfirmDialogComponent, {
        maxWidth: "400px",
        data: dialogData
      }).afterClosed().toPromise();

      if (result) {
        this.updateIncludedRecords(Array.from(this.includedIds));
      }
    } else {
      this.updateIncludedRecords(Array.from(this.includedIds));
    }
  }

  initIncludedIds() {
    this.includedIds.clear();
    if (this.groupedData) {
      this.groupedData.forEach(shop => {
        shop.records.forEach((record: any) => {
          this.includedIds.add(record.temp_export_id);
        });
      });
    }
  }

  toggleInclude(record: any) {
    record.is_included = record.isChecked ? '1' : '0';
    this.updateSelectAllState();
  }

  toggleSelectAll(event: MatCheckboxChange) {
    const checked = event.checked;
    this.selectAll = checked;
    if (this.groupedData) {
      this.groupedData.forEach(shop => {
        shop.records.forEach((record: any) => {
          if (checked) {
            this.includedIds.add(record.temp_export_id);
          } else {
            this.includedIds.delete(record.temp_export_id);
          }
        });
      });
    }
  }

  isIndeterminate(): boolean {
    const allIds: number[] = [];
    if (this.groupedData) {
      this.groupedData.forEach(shop => {
        shop.records.forEach((record: any) => {
          allIds.push(record.temp_export_id);
        });
      });
    }
    return (
      this.includedIds.size > 0 &&
      this.includedIds.size < allIds.length
    );
  }

  private updateSelectAllState() {
    const allIds: number[] = [];
    if (this.groupedData) {
      this.groupedData.forEach(shop => {
        shop.records.forEach((record: any) => {
          allIds.push(record.temp_export_id);
        });
      });
    }
    this.selectAll = this.includedIds.size === allIds.length;
  }

  updateIncludedRecords(selectedIds: number[]) {
    console.log('Selected IDs to update:', selectedIds);
    this.attendenceExportService.updateAttendance(selectedIds).subscribe({
      next: (response) => {
        if (response.status === 'SUCCESS') {
          this.snackBarService.openSnackBar({
            type: 'success',
            title: 'Success',
            message: 'Updated successfully.'
          });
          // Emit proceed to parent to reload or show attendance-data-export page
          this.proceed.emit();
        } else {
          this.snackBarService.openSnackBar({
            message: response.message || 'Update failed',
            title: 'Error',
            type: 'error'
          });
        }
      },
      error: (err) => {
        this.snackBarService.openSnackBar({
          message: 'Update failed due to server error',
          title: 'Error',
          type: 'error'
        });
      }
    });
  }

  // Add this method to handle checkbox changes from the template
  onIncludeChange(record: any, event: MatCheckboxChange) {
    const checked = event.checked;
    if (checked) {
      this.includedIds.add(record.temp_export_id);
    } else {
      this.includedIds.delete(record.temp_export_id);
    }
    this.updateSelectAllState();
  }

  isIncluded(record: any): boolean {
    return this.includedIds.has(record.temp_export_id);
  }
}
